import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { MapPin, Car, ShieldCheck, Loader2 } from "lucide-react";
import { LiveMap } from "@/components/LiveMap";
import { Logo } from "@/components/Logo";

const STATUS_LABEL = {
  assigned: "Driver on the way to pickup",
  in_progress: "Trip in progress",
  completed: "Trip completed",
  awaiting_assignment: "Assigning a driver",
};

export default function SharedRideTrack() {
  const { token } = useParams();
  const [info, setInfo] = useState(undefined);

  useEffect(() => {
    let alive = true;
    const load = () => axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/ride-track/${token}`)
      .then((r) => alive && setInfo(r.data)).catch(() => alive && setInfo(null));
    load();
    const t = setInterval(load, 5000);
    return () => { alive = false; clearInterval(t); };
  }, [token]);

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6" data-testid="shared-ride-track">
      <div className="mx-auto max-w-lg">
        <div className="mb-4 flex items-center justify-between">
          <Logo variant="sm" />
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700"><ShieldCheck className="h-3.5 w-3.5" /> Shared live trip</span>
        </div>
        {info === undefined ? (
          <div className="flex h-64 items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : info === null ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center" data-testid="shared-ride-invalid">
            <MapPin className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-3 font-semibold text-slate-700">Trip link unavailable</p>
            <p className="mt-1 text-sm text-slate-500">This live trip link has expired or is no longer valid.</p>
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="shared-ride-card">
            <p className="font-bold text-slate-900">{info.service_name || "Ride"}</p>
            {info.request_summary && <p className="mt-0.5 text-xs text-slate-500"><MapPin className="mr-1 inline h-3 w-3" />{info.request_summary}</p>}
            <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-sky-100 px-2.5 py-1 text-[11px] font-semibold text-sky-700" data-testid="shared-ride-status">{STATUS_LABEL[info.ride_status] || info.ride_status}</span>
            {(info.driver_name || info.vehicle) && (
              <p className="mt-2 flex items-center gap-1.5 text-sm text-slate-600"><Car className="h-4 w-4 text-slate-400" /> {[info.driver_name, info.vehicle].filter(Boolean).join(" · ")}</p>
            )}
            {info.waiting && (
              <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="shared-ride-waiting">
                <p className="text-sm font-bold text-amber-800">Waiting at pickup · {info.waiting.minutes} min</p>
                <p className="mt-0.5 text-[11px] text-amber-700">{info.waiting.in_grace
                  ? `Free grace period — ${info.waiting.grace_left} min left before waiting charges begin.`
                  : `Waiting charge so far: ₹${info.waiting.charge_so_far} (₹${info.waiting.rate}/${info.waiting.unit === "hr" ? "hr" : info.waiting.unit === "15min" ? "15 min" : "min"}, after ${info.waiting.grace_minutes} min grace).`}</p>
              </div>
            )}
            {info.live ? (
              <div className="mt-3">
                <LiveMap shareToken={token} destText={(info.request_summary || "").split(/→|->/).pop()?.trim()} />
                <p className="mt-1 text-[10px] text-slate-400">Live location shared by the rider. Updates every few seconds.</p>
              </div>
            ) : (
              <p className="mt-3 rounded-xl bg-slate-50 px-3 py-4 text-center text-sm text-slate-500" data-testid="shared-ride-ended">The live location is only available while the trip is active.</p>
            )}
          </div>
        )}
        <p className="mt-4 text-center text-[11px] text-slate-400">Powered by Suffle Online — safe travels.</p>
      </div>
    </div>
  );
}
